<template>
  <Dialog
      :header="t('TRANSFER_PERSON_FROM_GROUP', { name: transferTraineeModalData.trainee?.fullName })"
      v-model:visible="visible"
      modal
      class="transfer-modal overflow-hidden"
      :draggable="false"
      :dismissable-mask="true"
      @show="getGroupsForTransfer"
      @hide="resetTransferModal"
  >
    <form ref="transferFormRef" @submit.prevent="handleTransferModalSubmit">
      <div class="mb-2">
        <label for="select-destination" class="form-label text18-black">{{ t('GROUP') }}</label>
        <Select
            v-model="transferTraineeModalData.destination"
            :options="groupOptions"
            :option-disabled="(opt) => opt.disabled"
            label-id="select-destination"
            name="select-destination"
            option-label="text"
            option-value="value"
            :placeholder="t('SELECT')"
            class="w-100"
        />
      </div>

      <div class="mb-2">
        <label for="reason" class="form-label text18-black">{{ t('NOTE') }}</label>
        <Textarea
            v-model="transferTraineeModalData.reason"
            required
            id="reason"
            class="w-100"
            rows="3"
        />
      </div>
    </form>

    <confirm-overlay
        :show="showConfirm"
        :text="'TRANSFER_PERSON_FROM_GROUP_CONFIRM'"
        v-on:yes="handleConfirmOk"
        v-on:no="handleConfirmCancel"
    />

    <template #footer>
      <Button :label="t('CANCEL')" severity="secondary" @click="visible = false"/>
      <Button
          :label="t('CONFIRM')"
          :disabled="!transferTraineeModalData.destination || !transferTraineeModalData.reason"
          @click="handleTransferModalOk"
      />
    </template>
  </Dialog>
</template>

<script setup>
import EventBus from "@/util/eventBus";
import ConfirmOverlay from "@/views/components/modals/confirmOverlay.vue";
import {onMounted, onUnmounted, ref} from "vue";
import {injectAxios} from "@/util/inject";
import {useI18n} from "vue-i18n";

const emit = defineEmits(["transfer-trainee", "no", "yes"]);
const {t} = useI18n();
const axios = injectAxios();
const visible = ref(false);

const props = defineProps(["current"]);

const transferTraineeModalData = ref({
  trainee: null,
  numberOfTrainees: null,
  reason: null,
  destination: null,
  code: null,
});

const groupOptions = ref([]);
const transferFormRef = ref(null);
const showConfirm = ref(false);

onMounted(() => {
  EventBus.$on("show-transfer-trainee-modal", showTransferTraineeModal);
  getGroupsForTransfer();
});

onUnmounted(() => {
  EventBus.$off("show-transfer-trainee-modal", showTransferTraineeModal);
});

function resetTransferModal() {
  transferTraineeModalData.value = {
    trainee: null,
    numberOfTrainees: null,
    reason: null,
    destination: null,
    code: null,
  };
  showConfirm.value = false;
}

function showTransferTraineeModal(trainee, code) {
  transferTraineeModalData.value.trainee = trainee;
  transferTraineeModalData.value.code = code;
  visible.value = true;
}

function handleTransferModalOk(ev) {
  ev.preventDefault();
  handleTransferModalSubmit();
}

function handleTransferModalSubmit() {
  if (!transferFormRef.value?.checkValidity()) {
    return;
  }

  showConfirm.value = true;
}

function handleConfirmCancel() {
  showConfirm.value = false;
}

function handleConfirmOk() {
  emit("transfer-trainee", transferTraineeModalData.value);
  visible.value = false;
}

async function getGroupsForTransfer() {
  const code = transferTraineeModalData.value.code;
  if (!code) {
    return;
  }

  const resp = await axios.get(`/training/group/transferable/${code}`);
  const enabledOptions = [];
  const disabledOptions = [];

  resp.data.forEach((row) => {
    if (row.id === props.current) {
      return;
    }

    const traineeCount = row.numberOfTrainees ?? 0;
    const text = `${row.title} (${traineeCount}/${row.groupSize})`;
    const disabled = traineeCount >= row.groupSize;
    const entry = {value: row.id, text: text, disabled: disabled};

    if (disabled) {
      disabledOptions.push(entry);
    } else {
      enabledOptions.push(entry);
    }
  });

  groupOptions.value = enabledOptions.concat(disabledOptions);
}
</script>
